import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { CATEGORIES } from "@/lib/categories";

const BASE = "https://sellavo.com";

export const Route = createFileRoute("/kopa-och-salja/$ort")({
  loader: async ({ params }) => {
    // Ortsdatan är stor och behövs bara här – hämtas som eget paket.
    const { findCity } = await import("@/lib/orter-seo");
    const city = findCity(params.ort);
    if (!city) throw notFound();
    return { city };
  },
  head: ({ params, loaderData }) => {
    const city = loaderData?.city;
    if (!city) {
      return {
        meta: [{ title: "Orten finns inte | Sellavo" }, { name: "robots", content: "noindex" }],
      };
    }
    const title = `Köpa och sälja begagnat i ${city.name} | Sellavo`;
    const description = `Så köper och säljer du begagnat i ${city.name}. Fota prylen, få färdig annonstext och prisförslag – hämtning på plats eller frakt över hela landet.`;
    const url = `${BASE}/kopa-och-salja/${params.ort}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: city.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Start", item: BASE },
              { "@type": "ListItem", position: 2, name: "Orter", item: `${BASE}/kopa-och-salja` },
              { "@type": "ListItem", position: 3, name: city.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: CityPage,
  notFoundComponent: () => (
    <main className="mx-auto w-full max-w-4xl px-4 py-16 text-center sm:px-6 lg:px-8">
      <h1 className="font-heading text-2xl font-extrabold text-foreground">Orten finns inte</h1>
      <Button asChild className="mt-6">
        <Link to="/kopa-och-salja">Se alla orter</Link>
      </Button>
    </main>
  ),
});

function CityPage() {
  const { city } = Route.useLoaderData();

  return (
    <main className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <nav aria-label="Brödsmulor" className="text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Start
        </Link>
        <span className="px-1.5">/</span>
        <Link to="/kopa-och-salja" className="hover:text-foreground">
          Orter
        </Link>
      </nav>

      <h1 className="mt-3 font-heading text-3xl font-extrabold leading-tight tracking-tight text-foreground sm:text-4xl">
        Köpa och sälja begagnat i {city.name}
      </h1>
      <p className="mt-2 text-xs text-muted-foreground">{city.lan}</p>

      <div className="mt-6 rounded-lg border border-primary/25 bg-primary/5 p-5">
        <p className="font-heading text-[11px] font-bold uppercase tracking-wide text-primary">
          Det korta svaret
        </p>
        <p className="mt-2 text-sm leading-relaxed text-foreground sm:text-base">{city.summary}</p>
      </div>

      <div className="mt-6 space-y-4">
        {city.intro.map((p) => (
          <p key={p} className="text-base leading-relaxed text-muted-foreground">
            {p}
          </p>
        ))}
      </div>

      <div className="mt-10 space-y-8">
        {city.sections.map((section) => (
          <section key={section.heading}>
            <h2 className="font-heading text-xl font-bold text-foreground">{section.heading}</h2>
            {section.body.map((p) => (
              <p
                key={p}
                className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base"
              >
                {p}
              </p>
            ))}
          </section>
        ))}
      </div>

      <section className="mt-12">
        <h2 className="font-heading text-xl font-bold text-foreground">
          Vad säljer folk i {city.name}?
        </h2>
        <ul className="mt-4 flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <li key={c.slug}>
              <Link
                to="/salja/$kategori"
                params={{ kategori: c.slug }}
                className="inline-flex rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/50"
              >
                {c.label}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-12">
        <h2 className="font-heading text-xl font-bold text-foreground">Vanliga frågor</h2>
        <dl className="mt-4 space-y-4">
          {city.faq.map((item) => (
            <div key={item.q} className="rounded-lg border border-border bg-card p-5">
              <dt className="font-heading text-base font-semibold text-foreground">{item.q}</dt>
              <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.a}</dd>
            </div>
          ))}
        </dl>
      </section>

      <div className="mt-12 rounded-lg border border-border bg-card p-6 text-center">
        <p className="font-heading text-lg font-bold text-foreground">
          Sälj din pryl i {city.name}
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          Fota prylen så skriver vi annonsen och föreslår pris åt dig.
        </p>
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <Button asChild>
            <Link to="/skapa">Skapa annons</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/annonser">Se alla annonser</Link>
          </Button>
        </div>
      </div>
    </main>
  );
}
